import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import DiscoveryForm from "@/screens/DiscoveryForm";
import DubsadoEmbed from "@/screens/DubsadoEmbed";
import { COPY } from "@/config/copy";
import { appendParams } from "./auditParams";

/**
 * The dark "Ready to close it?" block with prescription details + CTA(s).
 * Hidden CTA in readonly mode (public shareable report).
 */
export default function PrescriptionBlock({
  primary,
  offer,
  critique,
  auditParams,
  readonly,
  assessmentId,
}) {
  const r = COPY.results;
  const cta = COPY.cta;
  const disc = COPY.discovery;
  const [showSecondary, setShowSecondary] = useState(false);

  const showExternalBtn = cta.mode === "external_url" || cta.mode === "both";
  const secondaryMode = disc.enabled ? disc.mode : "off";
  const showSecondaryOption =
    (cta.mode === "inline_form" || cta.mode === "both" || cta.mode === "external_url") &&
    secondaryMode !== "off";

  const openExternal = () => {
    const url = appendParams(cta.external.url, auditParams);
    if (cta.external?.open_in_new_tab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  return (
    <div className="bg-[#0A0A0A] text-[#F9F9F6] py-24 md:py-32" data-testid="section-prescription">
      <div className="stage">
        <div className="section-label mb-6" style={{ color: "#9fa5a3" }}>
          {r.prescription_block.eyebrow}
        </div>
        <h2 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] mb-8 display-tight">
          {r.prescription_block.headline}
        </h2>

        <p className="font-serif italic text-xl md:text-2xl text-[#D4D4D4] leading-[1.55] max-w-2xl mx-auto mb-14 display-tight">
          {critique.prescription_note}
        </p>

        <div className="border-t border-[#333] pt-14">
          <div className="section-label mb-4" style={{ color: "#9fa5a3" }}>
            {r.prescription_block.the_prescription_label}
          </div>
          <div className="font-serif text-4xl md:text-5xl font-light mb-4 display-tight" data-testid="prescription-name">
            {offer?.name}
          </div>
          <p className="text-[16px] text-[#D4D4D4] leading-[1.7] max-w-xl mx-auto mb-3">{offer?.tagline}</p>
          <p className="text-[14px] text-[#9fa5a3] leading-[1.7] max-w-xl mx-auto italic">
            {r.prescription_block.best_for_prefix} {offer?.best_for}
          </p>
        </div>

        {!readonly && (
          <div className="mt-16">
            <AnimatePresence mode="wait">
              {!showSecondary ? (
                <motion.div
                  key="cta"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-6"
                >
                  {showExternalBtn && (
                    <button
                      className="btn-primary-light"
                      onClick={openExternal}
                      data-testid="cta-external-btn"
                    >
                      {cta.external.label}
                      <ArrowRight className="btn-arrow" size={16} strokeWidth={1.5} />
                    </button>
                  )}
                  {showSecondaryOption && (
                    <button
                      onClick={() => setShowSecondary(true)}
                      data-testid="cta-inline-toggle"
                      className="text-sm text-[#9fa5a3] hover:text-white transition-colors underline underline-offset-4 decoration-[#3a3a3a] hover:decoration-[#9fa5a3]"
                    >
                      {disc.embed_reveal_label || cta.inline_form_toggle_label}
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.div key="secondary" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-4">
                  {secondaryMode === "dubsado_embed" ? (
                    <div className="max-w-2xl mx-auto">
                      <DubsadoEmbed
                        src={appendParams(disc.dubsado_embed.src, auditParams)}
                        initialHeight={disc.dubsado_embed.initial_height}
                      />
                    </div>
                  ) : (
                    <DiscoveryForm assessmentId={assessmentId} offerName={offer?.name} onSubmitted={() => {}} />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
